"use client";

import type { LeaderboardRankingCardProps } from "./leaderboard-ranking-card.types";
export type { LeaderboardRankingCardProps } from "./leaderboard-ranking-card.types";
import { PODIUM_STYLES } from "./leaderboard-ranking-card.constants";
import { cn } from "../../lib/utils";
import {
  getLeaderboardInitials,
  LeaderboardDisplayName,
} from "./leaderboard-identity";
import { ProfileAvatar } from "./profile-avatar";





export function LeaderboardRankingCard({
  rank,
  name,
  followers,
  earnings,
  members,
  avatar,
  isAnonymous = false,
  periodLabel,
  medalIcon,
  className,
}: LeaderboardRankingCardProps) {
  const isPodiumRank = rank >= 1 && rank <= 3;
  const podiumStyle = isPodiumRank
    ? PODIUM_STYLES[rank as keyof typeof PODIUM_STYLES]
    : null;

  return (
    <div
      className={cn(
        "relative overflow-hidden flex items-center justify-between gap-2 rounded-[8px] border border-black bg-white shadow-[0_4px_0_0_#000000]",
        isPodiumRank ? "px-4 py-3.5" : "px-3 py-2.5",
        className
      )}
      style={{
        backgroundImage: isPodiumRank
          ? `linear-gradient(90deg, ${podiumStyle?.surface} 0%, rgba(255, 255, 255, 0) 14%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)`
          : "linear-gradient(90deg, rgba(233, 246, 254, 0.12) 0%, rgba(233, 246, 254, 0.12) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
      }}
    >
      <div className="relative z-10 flex min-w-0 flex-1 items-center gap-3">
        <div
          className={cn(
            "flex shrink-0 items-center justify-center",
            isPodiumRank ? "w-12" : "min-w-[48px]",
          )}
        >
          {isPodiumRank ? (
            <div className="flex h-10 items-center justify-center">
              <span
                className={cn(
                  "block text-[40px] font-black leading-[0.82] tracking-[-0.08em]",
                  podiumStyle?.rankText,
                )}
              >
                {rank}
              </span>
            </div>
          ) : medalIcon ? (
            <div className="h-8 w-8">{medalIcon}</div>
          ) : (
            <div className="text-[28px] font-semibold text-[#ED5AA9]">#{rank}</div>
          )}
        </div>
        <ProfileAvatar
          src={avatar}
          alt={isAnonymous ? "Anonymous creator avatar" : name}
          className={cn(
            "shrink-0 rounded-full border border-black",
            isPodiumRank ? "h-9 w-9" : "h-8 w-8",
          )}
          fallbackClassName={cn(
            "bg-[#F2F4F7] font-semibold text-[#475467]",
            isPodiumRank ? "text-xs" : "text-[11px]",
          )}
          logoClassName="h-[54%] w-[54%]"
          fallback={<span>{getLeaderboardInitials(name)}</span>}
        />
        <div className="min-w-0">
          <LeaderboardDisplayName
            name={name}
            isAnonymous={isAnonymous}
            className={cn(
              isAnonymous ? "" : "truncate whitespace-nowrap",
              isPodiumRank
                ? "text-[15px] font-semibold text-[#344054]"
                : "text-sm font-medium text-[#475467]",
            )}
          />
          <p className="text-xs text-[#667085]">{followers}</p>
        </div>
      </div>
      <div className="relative z-10 shrink-0 text-right">
        <p
          className={cn(
            "font-medium text-[#ED5AA9]",
            isPodiumRank ? "text-[18px]" : "text-base",
          )}
        >
          {earnings}
        </p>
        {members !== undefined ? (
          <p className="whitespace-nowrap text-[11px] font-medium text-[#475467] sm:text-sm">
            {members} {members === 1 ? "Member" : "Members"}
          </p>
        ) : null}
        {periodLabel ? (
          <p className="text-[10px] font-normal text-[#667085]">{periodLabel}</p>
        ) : null}
      </div>
    </div>
  );
}
